import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import AdminNav from "../components/adminNav";
import Time from "../components/time";
import { Link, useParams } from "react-router-dom";
import { hasPermission } from "../utils/permissionHelper";
import Access from "../components/access";

/* ================= helpers ================= */

const format12hUTC = (d) => {
  if (!d) return "";
  const h = d.getUTCHours();
  const m = d.getUTCMinutes();
  const period = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad2(m)} ${period}`;
};


const pad2 = (n) => String(n).padStart(2, "0");

const safeDate = (val) => {
  if (!val) return null;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDMY = (d) =>
  d ? `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}` : "";

const format12h = (d) => {
  if (!d) return "";
  const h = d.getHours();
  const m = d.getMinutes();
  const period = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad2(m)} ${period}`;
};

const formatDateTimeCell = (d) => (d ? `${formatDMY(d)} ${format12h(d)}` : "");

// convert decimal hours (e.g., 10.5) -> "HH:MM"
const decToHHMM = (decimalHrs) => {
  if (decimalHrs === null || decimalHrs === undefined || decimalHrs === "") return "";
  const n = Number(decimalHrs);
  if (Number.isNaN(n)) return String(decimalHrs);
  const hh = Math.floor(n);
  const mm = Math.round((n - hh) * 60);
  return `${pad2(hh)}:${pad2(mm)}`;
};

// fallback visual AM/PM when only decimal hours are stored
const fallback12hFromHour = (decimalHrs) => {
  const hhmm = decToHHMM(decimalHrs);
  if (!hhmm.includes(":")) return String(decimalHrs ?? "");
  const [hh, mm] = hhmm.split(":").map((x) => parseInt(x, 10));
  if (Number.isNaN(hh) || Number.isNaN(mm)) return String(decimalHrs ?? "");
  const t = new Date();
  t.setHours(hh, mm, 0, 0);
  return format12h(t);
};

// datetime diff -> {days, hours, minutes}
const diffParts = (start, end) => {
  if (!start || !end) return null;
  const ms = end - start;
  if (ms < 0) return null;
  const totalMin = Math.floor(ms / 60000);
  const days = Math.floor(totalMin / (60 * 24));
  const remMin = totalMin - days * 60 * 24;
  const hours = Math.floor(remMin / 60);
  const minutes = remMin - hours * 60;
  return { days, hours, minutes };
};

// decimal hours from decimal start/end; supports overnight if end < start
const decimalHoursFromHourFields = (startDec, endDec) => {
  if (startDec === null || startDec === undefined || startDec === "" ||
      endDec === null || endDec === undefined || endDec === "") return null;

  const s = Number(startDec);
  const e = Number(endDec);
  if (Number.isNaN(s) || Number.isNaN(e)) return null;

  // convert to minutes from midnight
  const sMin = Math.floor(s) * 60 + Math.round((s - Math.floor(s)) * 60);
  const eMin = Math.floor(e) * 60 + Math.round((e - Math.floor(e)) * 60);

  let mins = eMin - sMin;
  if (mins < 0) mins += 24 * 60; // overnight to next day
  return mins / 60;
};

// inclusive calendar day count
const daySpanCount = (start, end) => {
  if (!start || !end) return 0;
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.max(1, Math.round((e - s) / 86400000) + 1);
};

const Pill = ({ children, tone = "neutral" }) => {
  const styleMap = {
    neutral: { background: "#eef2ff", color: "#3730a3" },
    warn: { background: "#fff7ed", color: "#9a3412" },
    ok: { background: "#ecfdf5", color: "#065f46" },
  };
  const st = styleMap[tone] || styleMap.neutral;
  return (
    <span
      style={{
        ...st,
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "999px",
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.6,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
};

// requested working-total formatter
const formatWorkingTotal = (hoursDecimal) => {
  if (hoursDecimal === null || hoursDecimal === undefined) return "";
  const n = Number(hoursDecimal);
  if (Number.isNaN(n)) return "";

  if (n < 24) {
    const s = Number.isInteger(n) ? `${n}` : `${parseFloat(n.toFixed(1))}`;
    return `${s} hours`;
  }
  const days = Math.floor(n / 24);
  const rem = n - days * 24;
  if (Math.abs(rem) < 1e-6) return `${days} day${days > 1 ? "s" : ""}`;
  const remStr = Number.isInteger(rem) ? `${rem}` : `${parseFloat(rem.toFixed(1))}`;
  return `${days} day${days > 1 ? "s" : ""} ${remStr}h`;
};

// convenience to detect hour-based view for this screen
const isHourBasis = (b) => !["Km", "Kmhour", "Ton", "Month"].includes(String(b || "").trim());

/* ================= component ================= */

const Vehicle_working_hours_hire = () => {
  useAuthRedirect();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const startYear = 2007;

  const { vcode, vehno, vehname, branch, basis, vtype } = useParams();
  const [data, setData] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(localStorage.getItem("branch") || "");
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  const canView  = hasPermission("Vehicle Daily Progress Entry", "view");
  const canEntry = hasPermission("Vehicle Daily Progress Entry", "entry");
  const canEdit  = hasPermission("Vehicle Daily Progress Entry", "edit");
  const canDel   = hasPermission("Vehicle Daily Progress Entry", "del");

  useEffect(() => {
    if (selectedBranch && vcode) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch, selectedYear, selectedMonth, vcode]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get("https://rfs2.infraconsoft.net.in/api/vehicle_working_hours_hire", {
        params: {
          branch_code: selectedBranch,
          month: selectedMonth,
          year: selectedYear,
          vcode: vcode
        }
      });
      const list = response.data?.vehicles || [];
      setData(list);
      setFilteredData(list);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      Swal.fire("Error", "Failed to fetch vehicle working hours (hire).", "error");
    } finally {
      setLoading(false);
    }
  };

  // search
  const handleSearch = (e) => {
    const q = (e.target.value || "").toLowerCase();
    setSearchTerm(e.target.value);
    const filtered = (data || []).filter((item) =>
      (item.fld_location && item.fld_location.toLowerCase().includes(q)) ||
      (item.fld_vdate && String(item.fld_vdate).toLowerCase().includes(q)) ||
      (item.fld_branch && String(item.fld_branch).toLowerCase().includes(q))
    );
    setFilteredData(filtered);
  };

  // delete
  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`https://rfs2.infraconsoft.net.in/api/vehicle_position_hire/${id}`);
          setData((prev) => prev.filter((x) => x.fld_vid !== id));
          setFilteredData((prev) => prev.filter((x) => x.fld_vid !== id));
          Swal.fire("Deleted!", "Your record has been deleted.", "success");
        } catch (error) {
          console.error("Error deleting vehicle position:", error);
          Swal.fire("Error!", "Failed to delete vehicle position.", "error");
        }
      }
    });
  };

  const rows = useMemo(() => filteredData || [], [filteredData]);

  if (!canView) {
    return (
      <div id="admin-container">
        <AdminNav />
        <div className="content-right" style={{ padding: 20 }}>
          <Access />
        </div>
      </div>
    );
  }

  // shared cell styles (align start/end nicely)
  const cellStack = {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 6,
  };
  const dateChip = (highlight) => ({
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.6,
    background: highlight ? "#fff7ed" : "#eef2ff",
    color: highlight ? "#9a3412" : "#3730a3",
  });

  return (
    <div id="admin-container">
      <AdminNav />
      <div className="content-right">
        <section>
          <div className="back-div">
            <h2 className="add-txt">Hire Vehicle Position Entry</h2>
            <Link to="/vehicle_view_add_hire">
              <p className="back-button"><i className="fa-solid fa-backward"></i>Back</p>
            </Link>
          </div>
          <Time />
        </section>

        <div className="vehicle-class">
          <div>
            <p><b>Select your Month :</b></p>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="branch-select"
            >
              {[...Array(12).keys()].map((i) => (
                <option key={i + 1} value={i + 1}>{pad2(i + 1)}</option>
              ))}
            </select>
          </div>
          <div>
            <p><b>Select your Year:</b></p>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="branch-select"
            >
              {[...Array(currentYear - startYear + 1).keys()].map((i) => (
                <option key={startYear + i} value={startYear + i}>{startYear + i}</option>
              ))}
            </select>
          </div>
        </div>

        {canEntry && (
          <div className="add-new-container">
            <Link
              to={`/vehicle_position_entry_hire/${encodeURIComponent(basis)}/${encodeURIComponent(vtype)}/${encodeURIComponent(vcode)}/${encodeURIComponent(vehno)}/${encodeURIComponent(vehname)}/${encodeURIComponent(branch)}`}
            >
              <button className="add-new-btn">
                <i className="fa-solid fa-plus"></i> Add New
              </button>
            </Link>
          </div>
        )}

        <div className="vehicle-details">
          <p>Vehicle Code : <b>{vcode}</b></p>
          <p>Vehicle Number : <b>{vehno}</b></p>
          <p>Vehicle Status : <b>Hire - {basis}</b></p>
        </div>

        {/* Search Filter */}
        <div className="search-filter">
          <p>Search: </p>
          <input
            type="text"
            placeholder="Search by Location / Date / Branch"
            value={searchTerm}
            onChange={handleSearch}
            className="search-input"
          />
        </div>

        <div className="content-table">
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Date</th>

                
                {(basis === "Ton") && <th>No. of Tons</th>}

                {/* Hour vs KM family */}
                {isHourBasis(basis) ? (
                  <>
                    <th>Start</th>
                    <th>End</th>
                    <th>Working Total</th>
                  </>
                ) : (
                  <>
                    <th>Breaking Hour</th>
                    <th>Starting KM</th>
                    <th>Closing KM</th>
                  </>
                )}

                <th>Diesel Qty</th>
                <th>No of Loads</th>
                <th>Location</th>
                {canEdit && <th>Edit</th>}
                {canDel && <th>Delete</th>}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="loading-text">Loading data, please wait...</td>
                </tr>
              ) : rows.length > 0 ? (
                rows.map((item, index) => {
                  const startDT = safeDate(item.fld_start_dt);
                  const endDT = safeDate(item.fld_end_dt);

                  const startDateText = startDT ? formatDMY(startDT) : "";
                    const startTimeText = item.fld_sthrs
  ? fallback12hFromHour(item.fld_sthrs)      // show exactly what's stored (09 → 9:00 AM)
  : (startDT ? format12hUTC(startDT) : "");


                  const endDateText = endDT ? formatDMY(endDT) : "";
                  const endTimeText = item.fld_endhrs
  ? fallback12hFromHour(item.fld_endhrs)     // show exactly what's stored (18 → 6:00 PM)
  : (endDT ? format12hUTC(endDT) : "");


                  const spansDays = startDT && endDT ? daySpanCount(startDT, endDT) : 0;
                  const isMultiDay = spansDays >= 2;

                  // Working Total priority (hour-based only)
                  let totalHoursDec = null;
                  if (isHourBasis(basis)) {
                    if (item.fld_wohrs !== undefined && item.fld_wohrs !== null && item.fld_wohrs !== "") {
                      totalHoursDec = Number(item.fld_wohrs);
                    } else if (startDT && endDT) {
                      const p = diffParts(startDT, endDT);
                      if (p) totalHoursDec = p.days * 24 + p.hours + p.minutes / 60;
                    } else {
                      const hDec = decimalHoursFromHourFields(item.fld_sthrs, item.fld_endhrs);
                      if (hDec !== null) totalHoursDec = hDec;
                    }
                  }

                  return (
                    <tr key={item.fld_vid ?? index}>
                      <td>{index + 1}</td>
                      <td>{new Date(item.fld_vdate).toLocaleDateString()}</td>

                      {(basis === "Ton") && <td>{item.fld_tons}</td>}

                      {isHourBasis(basis) ? (
                        <>
                          {/* Start (aligned, date chip highlighted if multi-day) */}
                          <td style={{ verticalAlign: "top" }}>
                            <div style={cellStack}>
                              {startDateText && (
                                <span style={dateChip(isMultiDay)}>{startDateText}</span>
                              )}
                              <span style={{ fontWeight: 700 }}>{startTimeText || "—"}</span>
                              {isMultiDay && <Pill tone="warn">Multi-day</Pill>}
                            </div>
                          </td>

                          {/* End (aligned same as start) */}
                          <td style={{ verticalAlign: "top" }}>
                            <div style={cellStack}>
                              {endDateText && (
                                <span style={dateChip(isMultiDay)}>{endDateText}</span>
                              )}
                              <span style={{ fontWeight: 700 }}>{endTimeText || "—"}</span>
                            </div>
                          </td>

                          {/* Working Total */}
                          <td>
                            {totalHoursDec !== null ? (
                              <Pill tone="ok">{formatWorkingTotal(totalHoursDec)}</Pill>
                            ) : (
                              <span style={{ opacity: 0.65 }}>—</span>
                            )}
                          </td>
                        </>
                      ) : (
                        <>
                          {/* Km family */}
                          <td>{`${pad2(item?.fld_brehrs ?? 0)}:${pad2(item?.fld_bremin ?? 0)}`}</td>
                          <td>{item.fld_stkm}</td>
                          <td>{item.fld_endkm}</td>
                        </>
                      )}

                      <td>{item.fld_dieselqty}</td>
                      <td>{(basis === "Ton" || basis === "Month") ? item.fld_noloads : item.fld_noload}</td>
                      <td>{item.fld_location}</td>

                      {canEdit && (
                        <td>
                          <Link
                            to={`/vehicle_position_hire_edit/${encodeURIComponent(item.fld_vid)}/${encodeURIComponent(basis)}/${encodeURIComponent(vtype)}/${encodeURIComponent(vcode)}/${encodeURIComponent(vehno)}/${encodeURIComponent(vehname)}/${encodeURIComponent(branch)}`}
                          >
                            <i className="fa-solid fa-pen-to-square edit"></i>
                          </Link>
                        </td>
                      )}

                      {canDel && (
                        <td className="del">
                          <i
                            className="fa-solid fa-trash-can"
                            onClick={() => handleDelete(item.fld_vid)}
                          ></i>
                        </td>
                      )}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="12">No Data Available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Vehicle_working_hours_hire;
